import React, { type ReactNode } from 'react';

// Shared with backend validator: matches [label](url) Markdown links.
// Label forbids unescaped `]`; URL forbids whitespace and `)`.
const MARKDOWN_LINK_RE = /\[([^\]]+)\]\(([^)\s]+)\)/g;

export function isSafeHttpUrl(value: string | undefined | null): value is string {
  if (!value || typeof value !== 'string') return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  try {
    const parsed = new URL(trimmed);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:';
  } catch {
    return false;
  }
}

export type ValidateLinksResult =
  | { ok: true }
  | { ok: false; badUrl: string };

export function validateMarkdownLinks(text: string | null | undefined): ValidateLinksResult {
  if (!text) return { ok: true };
  for (const match of text.matchAll(MARKDOWN_LINK_RE)) {
    const url = match[2];
    if (!isSafeHttpUrl(url)) {
      return { ok: false, badUrl: url };
    }
  }
  return { ok: true };
}

export function renderSafeMarkdown(text: string | null | undefined): ReactNode {
  if (!text) return null;

  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;

  for (const match of text.matchAll(MARKDOWN_LINK_RE)) {
    const [full, label, url] = match;
    const start = match.index ?? 0;

    if (start > lastIndex) {
      nodes.push(text.slice(lastIndex, start));
    }

    if (isSafeHttpUrl(url)) {
      nodes.push(
        <a
          key={`lnk-${key++}`}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
        >
          {label}
        </a>,
      );
    } else {
      // Unsafe URL: render the raw markdown verbatim so the user sees what
      // they typed. React escapes string children by default — no HTML parse.
      nodes.push(full);
    }

    lastIndex = start + full.length;
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return <>{nodes}</>;
}
